"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, PhoneCall, ShieldCheck, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}
const faqs = [{
  question: "What should I do if an outlet feels hot or smells like burning plastic?",
  answer: "Immediately turn off the power to that circuit at your breaker box and contact a qualified local electrician. This indicates faulty wiring or an overloaded circuit and requires prompt professional inspection.",
  icon: Zap
}, {
  question: "How quickly can an electrical pro arrive?",
  answer: "Availability varies by local independent professional. When requesting service through our platform, you can specify urgency and ask your pro about their earliest availability.",
  icon: PhoneCall
}, {
  question: "Do independent pros offer warranties on repairs?",
  answer: "Warranty terms are set directly by each independent professional and equipment manufacturers. Confirm specific warranty coverage with your pro before work begins.",
  icon: ShieldCheck
}, {
  question: "Can pros help install smart home devices and lighting?",
  answer: "Yes, experienced local pros assist with installing smart switches, outlets, smart thermostats, and lighting fixtures to modernize your home safely.",
  icon: HelpCircle
}, {
  question: "How do I know if my home needs an electrical panel upgrade?",
  answer: "Signs you may need an upgrade include frequently tripping breakers, flickering lights, or insufficient capacity for modern appliances or EV charging. A local pro can assess your current panel.",
  icon: HelpCircle
}, {
  question: "Why do my circuit breakers keep tripping?",
  answer: "Breakers trip to protect your home from overloads or short circuits. If it happens frequently, it usually means a circuit is overloaded, or there is an issue with wiring or a connected device that needs evaluation.",
  icon: HelpCircle
}, {
  question: "How often should I have my home's electrical system inspected?",
  answer: "Many experts recommend an electrical safety inspection every 3-5 years, or whenever you purchase an older home, add major appliances, or notice irregular electrical behavior.",
  icon: HelpCircle
}];
export default function ElectricalSafetyFAQ() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="electricalsafetyfaq" className="bg-background py-16 md:py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        {}
        <motion.div ref={headerRef} initial={{
        opacity: 0,
        y: 20
      }} animate={isHeaderVisible ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="text-center mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4">
            <ShieldCheck className="mr-1 size-3" />
            Safety & Support
          </Badge>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-foreground tracking-tight mb-4">
            Electrical Safety & Service FAQs
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto">
            Find answers to common questions about electrical safety, our services, and how we keep your home protected.
          </p>
        </motion.div>

        {}
        <motion.div ref={contentRef} initial={{
        opacity: 0,
        y: 20
      }} animate={isContentVisible ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.6,
        delay: 0.2,
        ease: "easeOut"
      }} className="bg-card rounded-xl shadow-sm border border-border p-4 md:p-8">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => {
            const Icon = faq.icon;
            return <AccordionItem key={i} value={`item-${i}`} data-index={i}>
                  <AccordionTrigger className="text-base md:text-lg font-semibold hover:text-primary transition-colors text-left">
                    <span className="flex items-center gap-3">
                      <span className="bg-primary/10 text-primary p-2 rounded-md shrink-0 hidden sm:flex">
                        <Icon className="size-5" />
                      </span>
                      {faq.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base leading-relaxed sm:pl-14">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>;
          })}
          </Accordion>
        </motion.div>

        {}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="mt-12 text-center bg-secondary/5 rounded-xl p-8 border border-border/50">
          <h3 className="font-default text-xl font-semibold text-foreground mb-2">
            Still have questions?
          </h3>
          <p className="text-muted-foreground mb-6">
            Connect with independent electrical pros ready to answer your questions and provide estimates.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="#bookingrequest">
                <PhoneCall className="mr-2 size-4" />
                Request a Quote
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="#bookingrequest">Find a Pro Near You</Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}